import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminBackfillStarted, AdminProjectCreate } from "@gb/admin-contracts";
import type { PhaseInput, ProcessingStep } from "@gb/contracts";
import { api, errorMessage } from "../../api/client";
import { documentKeys } from "../documents/documents-api";

export const projectKeys = {
  all: (tenantId: string) => ["tenants", tenantId, "projects"] as const,
  detail: (tenantId: string, projectId: string) =>
    [...projectKeys.all(tenantId), projectId] as const,
  phases: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "phases"] as const,
  jobs: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "jobs"] as const,
  backfill: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "backfill"] as const,
  brief: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "brief"] as const,
  imports: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "imports"] as const,
  importSteps: (tenantId: string, projectId: string, importId: string) =>
    [...projectKeys.imports(tenantId, projectId), importId, "steps"] as const,
  families: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "families"] as const,
  changes: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "changes"] as const,
  links: (tenantId: string, projectId: string) =>
    [...projectKeys.detail(tenantId, projectId), "links"] as const,
};

export function useProjects(tenantId: string) {
  return useQuery({
    queryKey: projectKeys.all(tenantId),
    queryFn: async () => {
      const res = await api.projects.list({ params: { tenantId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.projects;
    },
  });
}

export function useProject(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.detail(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.get({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
  });
}

export function usePhases(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.phases(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.phases({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.phases;
    },
  });
}

export function useJobs(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.jobs(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.jobs({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.jobs;
    },
  });
}

/**
 * The steps whose code has changed since they ran on some of the project's
 * files (2026-09-26, RET-09), with the files behind and what a backfill does.
 */
export function useBackfill(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.backfill(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.backfill({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.steps;
    },
  });
}

/**
 * Backfill one step: one job per file behind. A person starts it; nothing
 * starts it after a deploy. The queue and the list are re-read after it.
 */
export function useStartBackfill(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (step: ProcessingStep): Promise<AdminBackfillStarted> => {
      const res = await api.projects.startBackfill({ params: { tenantId, projectId }, body: { step } });
      if (res.status !== 202) throw new Error(errorMessage(res));
      return res.body;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.backfill(tenantId, projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.jobs(tenantId, projectId) });
    },
  });
}

/**
 * The project's brief, and who wrote it and when (`document-ai/0017` §3).
 * Its own route since 2026-10-03: it was half of the outline's, and the
 * outline went when files moved to the tenant's categories
 * (`document-ai/0032`). The categories come from the Brain summary.
 */
export function useBrief(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.brief(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.brief({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/** Every bundle that arrived, newest first — the server's order, kept as it is. */
export function useImports(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.imports(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.imports({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.imports;
    },
  });
}

/**
 * The steps that ran over one import as a whole, such as the check, the brief
 * and the families.
 */
export function useImportSteps(tenantId: string, projectId: string, importId: string) {
  return useQuery({
    queryKey: projectKeys.importSteps(tenantId, projectId, importId),
    queryFn: async () => {
      const res = await api.projects.importSteps({ params: { tenantId, projectId, importId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.steps;
    },
  });
}

/**
 * Every family of two or more files, and the files on their own with a
 * question waiting for a person (`document-ai/0016`).
 */
export function useFamilies(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.families(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.families({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * Every link in every state, and how often a person removed each kind
 * (`document-ai/0024` §5). Read-only: a person removes a link in the dashboard.
 */
export function useLinks(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.links(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.links({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/** The change log, newest first — the server's order — with the undo rate. */
export function useChanges(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: projectKeys.changes(tenantId, projectId),
    queryFn: async () => {
      const res = await api.projects.changes({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * Undo a placement, as the tenant's own undo does (`architecture/0027`). The
 * entry it writes has no user. A 409 means the files changed since, it was
 * already undone, or its kind cannot be undone; the server's message says
 * which. Invalidating the document list also refreshes each document's own
 * page, whose keys sit under it.
 */
export function useUndoChange(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changeId: string) => {
      const res = await api.projects.undoChange({ params: { tenantId, projectId, changeId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    // Settled, not only on success: a 409 means what the page shows is out of
    // date, so it is read again either way. An undo moves files between roles
    // and families, so every view of them is refetched. The brief is not one:
    // only a family change can be undone (`UNDOABLE_CHANGE_KINDS`).
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: projectKeys.families(tenantId, projectId) }),
        queryClient.invalidateQueries({ queryKey: projectKeys.changes(tenantId, projectId) }),
        queryClient.invalidateQueries({ queryKey: documentKeys.list(tenantId, projectId) }),
      ]),
  });
}

export function useCreateProject(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminProjectCreate) => {
      const res = await api.projects.create({ params: { tenantId }, body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(tenantId) }),
  });
}

export function useSetPhases(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (phases: PhaseInput[]) => {
      const res = await api.projects.setPhases({
        params: { tenantId, projectId },
        body: { phases },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.phases;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: projectKeys.phases(tenantId, projectId) }),
  });
}
